import { AuthError, getAuth } from 'firebase/auth'
import { FormEvent, useEffect, useState } from 'react'
import { useSetTitle } from '../../app/hooks/UseSetTitle.ts'
import {
  useGetUserAchievementsQuery,
  useUpdateUserNameMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useUpdatePassword } from 'react-firebase-hooks/auth'
import XIcon from '../../components/icons/xIcon.tsx'
import ClubAchievementsCard from './ClubAchievementsCard.tsx'
import ClubAchievementsCardSkeleton from './ClubAchievementsCardSkeleton.tsx'
import FavoriteClubSection from './FavoriteClubSection.tsx'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'

const resolveAchievementsErrorMessage = (
  error: FetchBaseQueryError | SerializedError | undefined
): string => {
  if (!error) {
    return 'Please try again in a moment.'
  }

  if ('status' in error) {
    const data = error.data
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      return String((data as { message?: string }).message)
    }
    return 'We were unable to load your achievements.'
  }

  if (error.message) {
    return error.message
  }

  return 'We were unable to load your achievements.'
}

const Profile = () => {
  useSetTitle('Profile')

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')

  const [triggeredNameUpdate, setTriggeredNameUpdate] = useState(false)
  const [nameUpdateSuccess, setNameUpdateSuccess] = useState(false)
  const [nameUpdateError, setNameUpdateError] = useState(false)

  const [triggeredPasswordUpdate, setTriggeredPasswordUpdate] = useState(false)
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false)
  const [passwordUpdateError, setPasswordUpdateError] = useState(false)

  //Update name
  const [updateName, updateNameResult] = useUpdateUserNameMutation()

  useEffect(() => {
    if (
      !updateNameResult.isLoading &&
      !updateNameResult.isUninitialized &&
      triggeredNameUpdate
    ) {
      if (updateNameResult.isSuccess) {
        setNameUpdateSuccess(true)
        setTimeout(() => setNameUpdateSuccess(false), 3000)
        setTriggeredNameUpdate(false)
      }
      if (updateNameResult.isError) {
        setNameUpdateError(true)
        setTimeout(() => setNameUpdateError(false), 3000)
        setTriggeredNameUpdate(false)
      }
    }
  }, [triggeredNameUpdate, updateNameResult])

  const handleNameUpdate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setTriggeredNameUpdate(true)
    if (!name) {
      setNameUpdateError(true)
      setTimeout(() => setNameUpdateError(false), 3000)
      setTriggeredNameUpdate(false)
      return
    }
    updateName({ body: { name: name } })
  }

  //Update Password
  const [updatePassword, isUpdatingPassword, passwordUpdateErrorResult] =
    useUpdatePassword(getAuth())

  const {
    data: achievements,
    isLoading: isLoadingAchievements,
    isError: isAchievementsError,
    error: achievementsError,
  } = useGetUserAchievementsQuery()

  const clubs = achievements?.clubs ?? []
  const getRolesScheduled = (clubAchievement: (typeof clubs)[number]) =>
    clubAchievement.totals?.rolesScheduled ?? 0
  const clubsWithAchievements = clubs.filter(
    (clubAchievement) => getRolesScheduled(clubAchievement) > 0
  )
  const clubsWithoutAchievements = clubs.filter(
    (clubAchievement) => getRolesScheduled(clubAchievement) === 0
  )
  const shouldPrioritizeAchievements =
    clubs.length > 1 &&
    clubsWithAchievements.length > 0 &&
    clubsWithoutAchievements.length > 0
  const orderedClubs = shouldPrioritizeAchievements
    ? [...clubsWithAchievements, ...clubsWithoutAchievements]
    : clubs
  const showAchievementsSkeleton =
    isLoadingAchievements || !achievements || achievements.clubs.length === 0
  const achievementsErrorMessage = resolveAchievementsErrorMessage(
    (isAchievementsError && achievementsError) || undefined
  )

  useEffect(() => {
    if (!isUpdatingPassword && triggeredPasswordUpdate) {
      if (passwordUpdateErrorResult) {
        setPasswordUpdateError(true)
        setPasswordUpdateSuccess(false)
        setTimeout(() => setPasswordUpdateError(false), 3000)
        setTriggeredPasswordUpdate(false)
        return
      }
      if (!passwordUpdateErrorResult) {
        setPasswordUpdateSuccess(true)
        setPasswordUpdateError(false)
        setTimeout(() => setPasswordUpdateSuccess(false), 3000)
        setTriggeredPasswordUpdate(false)
        setPassword('')
      }
    }
  }, [isUpdatingPassword, passwordUpdateErrorResult, triggeredPasswordUpdate])

  const handlePasswordUpdate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setTriggeredPasswordUpdate(true)
    if (!password && password.length < 6) {
      setPasswordUpdateSuccess(false)
      setPasswordUpdateError(true)
      setPasswordUpdateError(true)
      setTimeout(() => setPasswordUpdateError(false), 3000)
      setTriggeredPasswordUpdate(false)
      return
    }
    await updatePassword(password)
  }

  return (
    <div className="divide-y divide-gray-900/10">
      <div className="pt-2 pb-16">
        <div className="max-w-7xl">
          <div className="mb-8">
            <h3 className="text-base font-semibold leading-7 text-gray-900">
              Personal Achievements
            </h3>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Track how often you take on meeting roles across your clubs
            </p>
          </div>
          {showAchievementsSkeleton ? (
            <ClubAchievementsCardSkeleton />
          ) : isAchievementsError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm">
              <p className="text-base font-semibold text-red-900">
                Unable to load achievements
              </p>
              <p className="mt-2 text-red-700">{achievementsErrorMessage}</p>
            </div>
          ) : clubs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
              <p className="text-base font-semibold text-gray-900">
                No achievements yet
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Participate in upcoming meetings and your roles will appear
                here.
              </p>
            </div>
          ) : (
            <div>
              {orderedClubs.map((achievement) => (
                <ClubAchievementsCard
                  key={achievement.club.id}
                  achievement={achievement}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        className="max-w-7xl py-8"
        style={{ borderTop: 'none', marginTop: '-80px' }}>
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Personal Information
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
          Change your name or leave as is to remain unchanged
        </p>
      </div>
      {/* Name Update Section */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 py-16 md:grid-cols-3">
        <div>
          <h2 className="text-base font-semibold leading-7">Change name</h2>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Change your name or leave as is to remain unchanged
          </p>
        </div>
        <div className="md:col-span-2">
          <form onSubmit={handleNameUpdate}>
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium leading-6 text-gray-900">
                  Name
                </label>
                <div className="mt-2">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    onChange={(e) => setName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex items-center">
              <button
                type="submit"
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Update name
              </button>
              {nameUpdateSuccess && (
                <div className="ml-6 flex items-center">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="ml-2 order-1 text-sm font-medium text-green-800 sm:my-0">
                    Name successfully updated!
                  </p>
                </div>
              )}
              {nameUpdateError && (
                <div className="ml-6 flex items-center">
                  <svg
                    className="h-5 w-5 text-red-800"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true">
                    <XIcon />
                  </svg>
                  <p className="ml-2 order-1 text-sm font-medium text-red-800 sm:my-0">
                    Error updating name
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      <FavoriteClubSection />
      {/* Password Update Section */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 py-16 md:grid-cols-3">
        <div>
          <h2 className="text-base font-semibold leading-7">Change password</h2>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Update password associated with your account
          </p>
        </div>
        {/* TODO: Fix spacing... */}
        <div className="md:col-span-2">
          <form onSubmit={handlePasswordUpdate}>
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900">
                  New password
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex items-center">
              <button
                type="submit"
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Change password
              </button>
              {passwordUpdateSuccess && (
                <div className="ml-6 flex items-center">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="ml-2 order-1 text-sm font-medium text-green-800 sm:my-0">
                    Password successfully updated!
                  </p>
                </div>
              )}
              {passwordUpdateError && (
                <div className="ml-6 flex items-center">
                  <svg
                    className="h-5 w-5 text-red-800"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true">
                    <XIcon />
                  </svg>
                  <p className="ml-2 order-1 text-sm font-medium text-red-800 sm:my-0">
                    {(() => {
                      switch ((passwordUpdateErrorResult as AuthError)?.code) {
                        case 'auth/weak-password':
                          return 'Password should be at least 6 characters'
                        case 'auth/requires-recent-login':
                          //TODO: Add re-authentication pop-up
                          return 'Please logout/login again to update your password'
                        default:
                          return 'There was an error updating your password'
                      }
                    })()}
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Profile
